import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, createProject, updateProject, deleteProject, downloadExcel, getDashboardStats } from '../services/api';
import { HiOutlineFolder, HiOutlineDocumentDownload, HiOutlineTrash, HiOutlineEye, HiOutlinePlus, HiOutlinePencil } from 'react-icons/hi';
import { FiGrid, FiAlertOctagon, FiTarget, FiCheckCircle, FiXCircle, FiTrendingUp, FiActivity, FiClock } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Modal, { ConfirmModal } from '../components/Modal';
import { useToast } from '../App';
import { useAuth } from '../contexts/AuthContext';

const PIE_COLORS_HEX = {
  OK: '#22c55e',
  KO: '#ef4444',
  EC: '#3b82f6',
  BL: '#f59e0b',
  NT: '#d1d5db',
};

const STATUS_LABELS = { OK: 'Reussi', KO: 'Echoue', EC: 'En Cours', BL: 'Bloque', NT: 'Non Teste' };

const ACTION_LABELS = {
  create: 'a cree',
  update: 'a modifie',
  delete: 'a supprime',
  update_statut: 'a change le statut de',
  update_commentaire: 'a commente',
};

const ENTITY_LABELS = {
  requirement: 'exigence',
  test_case: 'cas de test',
  defect: 'defaut',
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'white', border: '1px solid #e2e7f0', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.78rem',
    }}>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.fill, flexShrink: 0 }}></span>
          <span style={{ color: '#4A5568' }}>{p.name}:</span>
          <strong style={{ color: '#0F1B2D' }}>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard({ onProjectsChange }) {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', description: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editProject, setEditProject] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', description: '' });
  const addToast = useToast();

  const { user, isAdmin } = useAuth();

  const loadData = async () => {
    setLoading(true);
    try {
      const [projRes, statsRes] = await Promise.all([getProjects(), getDashboardStats()]);
      setProjects(projRes.data);
      setStats(statsRes.data);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    await createProject(form);
    setForm({ name: '', code: '', description: '' });
    setShowModal(false);
    loadData();
    if (onProjectsChange) onProjectsChange();
    addToast('success', 'Le projet a ete cree avec succes.', 'Projet cree');
  };

  const handleOpenEdit = (e, p) => {
    e.stopPropagation();
    setEditProject(p);
    setEditForm({ name: p.name, code: p.code, description: p.description || '' });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateProject(editProject.id, editForm);
      setEditProject(null);
      loadData();
      if (onProjectsChange) onProjectsChange();
      addToast('success', 'Le projet a ete modifie avec succes.', 'Projet modifie');
    } catch {
      addToast('error', 'Erreur lors de la modification du projet.');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await deleteProject(confirmDelete);
    setConfirmDelete(null);
    loadData();
    if (onProjectsChange) onProjectsChange();
    addToast('success', 'Le projet a ete supprime.', 'Supprime');
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <output className="spinner-border"></output>
        <span>Chargement...</span>
      </div>
    );
  }

  const sc = stats?.status_counts || { NT: 0, EC: 0, OK: 0, KO: 0, BL: 0 };
  const pieData = Object.entries(sc).filter(([, v]) => v > 0).map(([k, v]) => ({ name: STATUS_LABELS[k], value: v, key: k }));
  const barData = (stats?.project_stats || []).map(p => ({
    name: p.code || p.name.substring(0, 10),
    OK: p.ok,
    KO: p.ko,
    EC: p.ec,
    BL: p.bl,
    NT: p.nt,
  }));

  const alertProjects = (stats?.project_stats || []).filter(p => p.ko > 0 || p.open_defects > 0);

  const formatTimeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'A l\'instant';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon apres-midi';
    return 'Bonsoir';
  };

  return (
    <div className="dash-page">
      {/* Welcome Banner - fixed */}
      <div className="dash-welcome">
        <div className="d-flex align-items-start justify-content-between">
          <div>
            <h2>{greeting()}, {user?.full_name || user?.username}</h2>
            <p>Voici un apercu de vos cahiers de test</p>
          </div>
          {isAdmin && (
            <button className="btn btn-sm" onClick={() => setShowModal(true)}
                    style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <HiOutlinePlus /> Nouveau Projet
            </button>
          )}
        </div>
        <div className="dash-welcome-stats">
          <div>
            <div className="dash-welcome-stat-value">{stats?.projects || 0}</div>
            <div className="dash-welcome-stat-label">Projets</div>
          </div>
          <div>
            <div className="dash-welcome-stat-value">{stats?.requirements || 0}</div>
            <div className="dash-welcome-stat-label">Exigences</div>
          </div>
          <div>
            <div className="dash-welcome-stat-value">{stats?.test_cases || 0}</div>
            <div className="dash-welcome-stat-label">Cas de test</div>
          </div>
          <div>
            <div className="dash-welcome-stat-value">{stats?.execution_rate || 0}%</div>
            <div className="dash-welcome-stat-label">Execute</div>
          </div>
          <div>
            <div className="dash-welcome-stat-value">{stats?.success_rate || 0}%</div>
            <div className="dash-welcome-stat-label">Reussite</div>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="dash-scroll">

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <div className="dash-kpi-card">
            <div className="dash-kpi-icon" style={{ background: 'linear-gradient(135deg, #d5f5e3, #a7e8c4)' }}>
              <FiTrendingUp style={{ color: 'var(--green-700)' }} />
            </div>
            <div>
              <div className="dash-kpi-value">{stats?.execution_rate || 0}%</div>
              <div className="dash-kpi-label">Taux d'execution</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="dash-kpi-card">
            <div className="dash-kpi-icon" style={{ background: 'linear-gradient(135deg, #d5f5e3, #86efac)' }}>
              <FiCheckCircle style={{ color: 'var(--green-700)' }} />
            </div>
            <div>
              <div className="dash-kpi-value">{stats?.success_rate || 0}%</div>
              <div className="dash-kpi-label">Taux de reussite</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="dash-kpi-card">
            <div className="dash-kpi-icon" style={{ background: 'linear-gradient(135deg, var(--info-light), #93c5fd)' }}>
              <FiTarget style={{ color: 'var(--info)' }} />
            </div>
            <div>
              <div className="dash-kpi-value">{stats?.active_campaigns || 0}</div>
              <div className="dash-kpi-label">Campagnes actives</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="dash-kpi-card">
            <div className="dash-kpi-icon" style={{ background: 'linear-gradient(135deg, var(--danger-light), #fca5a5)' }}>
              <FiAlertOctagon style={{ color: 'var(--danger)' }} />
            </div>
            <div>
              <div className="dash-kpi-value">{stats?.open_defects || 0}</div>
              <div className="dash-kpi-label">Defauts ouverts</div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4">
        {/* Pie */}
        <div className="col-lg-4">
          <div className="dash-chart-card">
            <div className="dash-chart-title">Repartition des statuts</div>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={82}
                         paddingAngle={3} dataKey="value" strokeWidth={0}>
                      {pieData.map((entry) => (
                        <Cell key={entry.key} fill={PIE_COLORS_HEX[entry.key]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="dash-chart-legend">
                  {Object.entries(STATUS_LABELS).map(([k, label]) => (
                    <div key={k} className="dash-legend-item">
                      <span className="dash-legend-dot" style={{ background: PIE_COLORS_HEX[k] }}></span>
                      <span>{label}</span>
                      <span className="dash-legend-count">{sc[k] || 0}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-muted text-center py-5" style={{ fontSize: '0.82rem' }}>Aucune donnee</div>
            )}
          </div>
        </div>

        {/* Bar */}
        <div className="col-lg-8">
          <div className="dash-chart-card">
            <div className="dash-chart-title">Tests par projet</div>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={barData} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7A90' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7A90' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="OK" stackId="a" fill="#22c55e" name="Reussi" radius={[0,0,0,0]} />
                  <Bar dataKey="KO" stackId="a" fill="#ef4444" name="Echoue" />
                  <Bar dataKey="EC" stackId="a" fill="#3b82f6" name="En Cours" />
                  <Bar dataKey="BL" stackId="a" fill="#f59e0b" name="Bloque" />
                  <Bar dataKey="NT" stackId="a" fill="#d1d5db" name="Non Teste" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-muted text-center py-5" style={{ fontSize: '0.82rem' }}>Aucun projet</div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts + Activity */}
      <div className="row g-3 mb-4">
        <div className="col-lg-5">
          <div className="dash-chart-card">
            <div className="dash-chart-title d-flex align-items-center gap-2">
              <FiAlertOctagon style={{ color: 'var(--danger)', fontSize: '1rem' }} /> Projets en alerte
            </div>
            {alertProjects.length > 0 ? (
              <div className="dash-alert-list">
                {alertProjects.map(p => (
                  <Link key={p.id} to={`/projects/${p.id}`} className="dash-alert-item">
                    <div className="dash-alert-name">{p.name}</div>
                    <div className="dash-alert-tags">
                      {p.ko > 0 && (
                        <span className="dash-alert-tag dash-alert-ko">
                          <FiXCircle /> {p.ko} KO
                        </span>
                      )}
                      {p.open_defects > 0 && (
                        <span className="dash-alert-tag dash-alert-defect">
                          <FiAlertOctagon /> {p.open_defects} defaut{p.open_defects > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <FiCheckCircle style={{ fontSize: '2rem', color: 'var(--green-500)', display: 'block', margin: '0 auto 10px' }} />
                <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', fontWeight: 500 }}>Aucun projet en alerte</div>
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-7">
          <div className="dash-chart-card">
            <div className="dash-chart-title d-flex align-items-center gap-2">
              <FiActivity style={{ color: 'var(--navy-400)', fontSize: '1rem' }} /> Activite recente
            </div>
            {(stats?.recent_activity || []).length > 0 ? (
              <div className="dash-activity-list">
                {stats.recent_activity.map(a => (
                  <div key={a.id} className="dash-activity-item">
                    <div className="dash-activity-dot"></div>
                    <div className="dash-activity-content">
                      <span className="dash-activity-user">{a.performed_by}</span>
                      <span className="dash-activity-action">{ACTION_LABELS[a.action] || a.action}</span>
                      <span className="dash-activity-entity">
                        {ENTITY_LABELS[a.entity_type] || a.entity_type} <strong>{a.entity_label}</strong>
                      </span>
                    </div>
                    <span className="dash-activity-time">
                      <FiClock /> {formatTimeAgo(a.performed_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted text-center py-4" style={{ fontSize: '0.82rem' }}>Aucune activite recente</div>
            )}
          </div>
        </div>
      </div>

      {/* Projects section */}
      <div className="dash-section-title">Projets</div>
      <div className="row g-3">
        {projects.map((p) => {
          const ps = (stats?.project_stats || []).find(s => s.id === p.id);
          return (
            <div key={p.id} className="col-md-4">
              <div className="card project-card h-100" style={{ cursor: 'pointer', position: 'relative' }}>
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <h5 className="card-title mb-0">
                      <Link to={`/projects/${p.id}`} className="text-decoration-none text-navy stretched-link">
                        {p.name}
                      </Link>
                    </h5>
                    <span className="project-code">{p.code}</span>
                  </div>
                  {p.description && <p className="text-muted small mb-2">{p.description}</p>}
                  <div className="project-stats">
                    <div className="project-stat">
                      <div className="project-stat-value">{p.requirements_count || 0}</div>
                      <div className="project-stat-label">Exigences</div>
                    </div>
                    <div className="project-stat">
                      <div className="project-stat-value">{p.test_cases_count || 0}</div>
                      <div className="project-stat-label">Cas de Test</div>
                    </div>
                    {ps && (
                      <div className="project-stat">
                        <div className="project-stat-value" style={{ color: ps.success_rate >= 80 ? 'var(--green-600)' : ps.success_rate >= 50 ? 'var(--warning)' : 'var(--danger)' }}>
                          {ps.execution_rate}%
                        </div>
                        <div className="project-stat-label">Execute</div>
                      </div>
                    )}
                  </div>
                  {ps && ps.total_tcs > 0 && (
                    <div className="test-progress mt-2" style={{ height: '5px', borderRadius: 4 }}>
                      <div className="progress-ok" style={{ width: `${(ps.ok / ps.total_tcs) * 100}%` }}></div>
                      <div className="progress-ko" style={{ width: `${(ps.ko / ps.total_tcs) * 100}%` }}></div>
                      <div className="progress-ec" style={{ width: `${(ps.ec / ps.total_tcs) * 100}%` }}></div>
                      <div className="progress-bl" style={{ width: `${(ps.bl / ps.total_tcs) * 100}%` }}></div>
                      <div className="progress-nt" style={{ width: `${(ps.nt / ps.total_tcs) * 100}%` }}></div>
                    </div>
                  )}
                </div>
                <div className="card-footer d-flex gap-2" style={{ position: 'relative', zIndex: 1 }}>
                  <Link to={`/projects/${p.id}`} className="btn btn-outline-primary btn-sm">
                    <HiOutlineFolder /> Ouvrir
                  </Link>
                  <Link to={`/projects/${p.id}/preview`} className="btn btn-outline-info btn-sm">
                    <HiOutlineEye /> Preview
                  </Link>
                  <button className="btn btn-outline-success btn-sm" onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const { data } = await downloadExcel(p.id);
                      const url = URL.createObjectURL(data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${p.name || 'project'}.xlsx`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { addToast('error', 'Erreur lors du telechargement'); }
                  }}>
                    <HiOutlineDocumentDownload /> Excel
                  </button>
                  {isAdmin && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={(e) => handleOpenEdit(e, p)} title="Modifier le projet">
                      <HiOutlinePencil />
                    </button>
                  )}
                  {isAdmin && (
                    <button className="btn btn-outline-danger btn-sm ms-auto" onClick={() => setConfirmDelete(p.id)}>
                      <HiOutlineTrash />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {projects.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon"><FiGrid /></div>
          <p>{isAdmin ? 'Aucun projet. Creez un nouveau projet pour commencer.' : 'Aucun projet assigne.'}</p>
          {isAdmin && (
            <button className="btn btn-primary btn-sm mt-3" onClick={() => setShowModal(true)}>
              <HiOutlinePlus /> Nouveau Projet
            </button>
          )}
        </div>
      )}

      </div>{/* end dash-scroll */}

      <Modal show={showModal} onClose={() => setShowModal(false)} title="Nouveau Projet" icon={<HiOutlinePlus />}>
        <form onSubmit={handleCreate}>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-project-name">Nom du projet</label>
            <input id="field-project-name" className="form-control" placeholder="Ex: Delta-Bank - Retrait Especes"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus maxLength={200} />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-project-code">Code</label>
            <input id="field-project-code" className="form-control" placeholder="Ex: guretesp"
                   value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required maxLength={50} />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-project-description">Description</label>
            <textarea id="field-project-description" className="form-control" rows="2" placeholder="Description du projet"
                   value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm">Creer le projet</button>
          </div>
        </form>
      </Modal>

      <Modal show={!!editProject} onClose={() => setEditProject(null)} title="Modifier le projet" icon={<HiOutlinePencil />}>
        <form onSubmit={handleUpdate}>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-project-name">Nom du projet</label>
            <input id="field-edit-project-name" className="form-control" placeholder="Ex: Delta-Bank - Retrait Especes"
                   value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required autoFocus maxLength={200} />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-project-code">Code</label>
            <input id="field-edit-project-code" className="form-control" placeholder="Ex: guretesp"
                   value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} required maxLength={50} />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-project-description">Description</label>
            <textarea id="field-edit-project-description" className="form-control" rows="2" placeholder="Description du projet"
                   value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditProject(null)}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm">Enregistrer</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le projet"
        message="Etes-vous sur de vouloir supprimer ce projet ? Toutes les exigences et cas de test seront perdus."
        confirmText="Supprimer"
      />
    </div>
  );
}
